export const SERVICE_MEDIA_BUCKET = "service-media";
export const MAX_SERVICE_COVER_BYTES = 8 * 1024 * 1024;
export const MAX_SERVICE_DOCUMENT_BYTES = 15 * 1024 * 1024;
export const SERVICE_COVER_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
] as const;
export const SERVICE_DOCUMENT_TYPES = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-powerpoint",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "text/plain",
] as const;

export type ServiceRequestStatus =
  | "new"
  | "awaiting_contact"
  | "contacted"
  | "information_requested"
  | "offer_preparation"
  | "offer_sent"
  | "accepted"
  | "rejected"
  | "contracted"
  | "closed"
  | "cancelled";

export type ServiceCategory = {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  sortOrder: number;
};

export type ServiceFaqItem = {
  question: string;
  answer: string;
};

export type ServiceDocument = {
  id: string;
  title: string;
  fileName: string;
  sizeBytes: number;
  url: string | null;
};

export type ServiceCtaType =
  | "interest"
  | "quote"
  | "consultation"
  | "start"
  | "order";

export type CatalogService = {
  id: string;
  slug: string;
  name: string;
  summary: string;
  description: string;
  iconName: string | null;
  iconColor: string | null;
  coverUrl: string | null;
  ctaType: ServiceCtaType;
  ctaLabel: string;
  pricingModel: string;
  priceAmount: number | null;
  priceUnit: string | null;
  currency: string;
  benefits: string[];
  audienceSummary: string | null;
  problemStatement: string | null;
  faq: ServiceFaqItem[];
  documents: ServiceDocument[];
  category: ServiceCategory;
  partnerName: string | null;
  isFeatured: boolean;
  termsText: string | null;
  termsVersion: string | null;
};

export type UserPractice = {
  id: string;
  name: string;
  city: string;
  isPrimary: boolean;
};

export type ServiceRequestTopic =
  | "orientation"
  | "quote"
  | "consultation"
  | "existing"
  | "other";

export type ContactMethod = "phone" | "email";

export type ServiceRequestFormValues = {
  practiceId: string;
  topic: ServiceRequestTopic;
  description: string;
  contactMethod: ContactMethod;
  contactPhone: string;
  preferredContactAt: string;
};

export type ServiceRequestListItem = {
  id: string;
  status: ServiceRequestStatus;
  createdAt: string;
  updatedAt: string;
  serviceName: string;
  serviceSlug: string;
  practiceName: string;
  practiceCity: string | null;
};

export const SERVICE_REQUEST_STATUS_LABELS: Record<ServiceRequestStatus, string> = {
  new: "Új",
  awaiting_contact: "Kapcsolatfelvételre vár",
  contacted: "Feldolgozás alatt",
  information_requested: "Feldolgozás alatt",
  offer_preparation: "Ajánlat készül",
  offer_sent: "Ajánlat elküldve",
  accepted: "Elfogadva",
  rejected: "Elutasítva",
  contracted: "Szerződött",
  closed: "Lezárt",
  cancelled: "Visszavonva",
};

export const SERVICE_REQUEST_TOPICS: { value: ServiceRequestTopic; label: string }[] = [
  { value: "orientation", label: "Tájékozódás" },
  { value: "quote", label: "Ajánlatkérés" },
  { value: "consultation", label: "Tanácsadás" },
  { value: "existing", label: "Meglévő ügy / szerződés" },
  { value: "other", label: "Egyéb" },
];

export function formatServicePrice(service: {
  pricingModel: string;
  priceAmount: number | null;
  priceUnit?: string | null;
  currency: string;
}) {
  if (service.pricingModel === "free") {
    return "Ingyenes";
  }
  if (service.priceAmount !== null) {
    const unit = service.priceUnit?.trim();
    return unit
      ? `${service.priceAmount.toLocaleString("hu-HU")} ${service.currency} ${unit}`
      : `${service.priceAmount.toLocaleString("hu-HU")} ${service.currency}`;
  }
  return "Egyedi ajánlat";
}
